class FormHandler {
  constructor(formSelector) {
    this.form = document.querySelector(formSelector);
    if (!this.form) return;
    this.fieldWrappers = this.form.querySelectorAll('.field');
    this.selects = this.form.querySelectorAll('.select');
    this.submitBtn = this.form.querySelector('.btn');
    this.ajaxUrl = window.ajax_url;

    this._toggleActive = this._toggleActive.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);

    this._initFields();
    this._initSelectDropdowns();
    this._bindGlobalClick();
    this._initFormSubmission();
  }

  // Toggle .is-active on the field wrapper
  _toggleActive(wrapper, active) {
    wrapper.classList[active ? 'add' : 'remove']('is-active');
  }

  // Initialize inputs, textareas, and selects for floating labels and focus state
  _initFields() {
    this.fieldWrappers.forEach(wrapper => {
      const input = wrapper.querySelector('input, textarea, select');
      if (!input) return;

      // Initial active state
      this._toggleActive(wrapper, Boolean(input.value));

      if (input.tagName === 'SELECT') {
        // for native select sync only
        if (input.value) wrapper.classList.add('is-active');
      } else {
        // Input & textarea focus/blur/input
        input.addEventListener('focus', () => {
          wrapper.classList.add('is-focused');
          this._toggleActive(wrapper, true);
        });

        input.addEventListener('blur', () => {
          wrapper.classList.remove('is-focused');
          this._toggleActive(wrapper, Boolean(input.value));
        });

        input.addEventListener('input', () => this._toggleActive(wrapper, Boolean(input.value)));
      }
    });
  }

  // Setup custom select dropdown accessibility and interaction
  _initSelectDropdowns() {
    this.selects.forEach(select => {
      const wrapper = select.closest('.field');
      const trigger = select.querySelector('.select__value');
      const dropdown = select.querySelector('.select__dropdown');
      const nativeSelect = select.querySelector('select');
      const options = Array.from(dropdown.querySelectorAll('[role="option"]'));

      // initialize aria and hidden state
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.setAttribute('role', 'listbox');
      dropdown.hidden = true;
      options.forEach(opt => {
        opt.setAttribute('tabindex', '-1');
      });

      // open dropdown
      this._openDropdown = sel => {
        const btn = sel.querySelector('.select__value');
        const dd = sel.querySelector('.select__dropdown');
        const wp = sel.closest('.field');
        this.selects.forEach(s => {
          if (s !== sel) this._closeDropdown(s);
        });
        sel.classList.add('active');
        btn.setAttribute('aria-expanded', 'true');
        dd.hidden = false;
        wp.classList.add('is-focused');
        this._toggleActive(wp, true);
      };

      // close dropdown
      this._closeDropdown = sel => {
        const btn = sel.querySelector('.select__value');
        const dd = sel.querySelector('.select__dropdown');
        const wp = sel.closest('.field');
        sel.classList.remove('active');
        btn.setAttribute('aria-expanded', 'false');
        dd.hidden = true;
        wp.classList.remove('is-focused');
        this._toggleActive(wp, Boolean(sel.querySelector('select').value));
      };

      // toggle dropdown
      this._toggleDropdown = sel => {
        if (sel.classList.contains('active')) this._closeDropdown(sel);
        else this._openDropdown(sel);
      };

      // click trigger
      trigger.addEventListener('click', e => {
        e.stopPropagation();
        this._toggleDropdown(select);
      });

      // keyboard on trigger
      trigger.addEventListener('keydown', e => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            this._openDropdown(select);
            options[0]?.focus();
            break;
          case 'ArrowUp':
            e.preventDefault();
            this._openDropdown(select);
            options[options.length - 1]?.focus();
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            this._toggleDropdown(select);
            break;
        }
      });

      // option interactions
      options.forEach((opt, idx) => {
        opt.addEventListener('click', e => {
          e.preventDefault();
          const val = opt.getAttribute('data-value');
          nativeSelect.value = val;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          select.querySelector('.select__value span').textContent = opt.textContent;
          this._closeDropdown(select);
        });
        opt.addEventListener('keydown', e => {
          let next, prev;
          switch (e.key) {
            case 'ArrowDown':
              e.preventDefault();
              next = options[idx + 1] || options[0];
              next.focus();
              break;
            case 'ArrowUp':
              e.preventDefault();
              prev = options[idx - 1] || options[options.length - 1];
              prev.focus();
              break;
            case 'Enter':
            case ' ':
              e.preventDefault();
              opt.click();
              break;
            case 'Escape':
              e.preventDefault();
              trigger.focus();
              this._closeDropdown(select);
              break;
          }
        });
      });
    });
  }

  // Close selects when clicking outside
  _bindGlobalClick() {
    document.addEventListener('click', this._onDocumentClick);
  }

  _onDocumentClick(e) {
    if (!e.target.closest('.select')) {
      this.selects.forEach(s => this._closeDropdown(s));
    }
  }

  // Form submission handling
  _initFormSubmission() {
    this.form.addEventListener('submit', async e => {
      e.preventDefault();
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }
      const recaptcha = this.form.querySelector('[name="g-recaptcha-response"]');
      if (recaptcha && !recaptcha.value) return;

      const formData = new FormData(this.form);
      formData.append('action', 'form_response');

      this.submitBtn.disabled = true;
      const originalText = this.submitBtn.value || this.submitBtn.textContent;
      this.submitBtn.value = this.submitBtn.textContent = 'Sending...';

      try {
        const res = await fetch(this.ajaxUrl, {
          method: 'POST',
          body: new URLSearchParams(formData)
        });
        const result = await res.json();
        if (result && typeof result === 'object') {
          result.success ? this._onSuccess(result.success) : this._onError(result.error);
        } else {
          console.error('Unexpected response:', result);
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        this.submitBtn.disabled = false;
        this.submitBtn.value = this.submitBtn.textContent = originalText;
      }
    });
  }

  // Display field errors and form-level error
  _onError(response) {
    const existing = this.form.querySelector('.form__error');
    if (existing) existing.remove();

    Object.values(response.fields).forEach(field => {
      if (['hidden','honeypot'].includes(field.type)) return;
      const input = this.form.querySelector(`[name="${field.name}"]`);
      const container = input.closest('.field, .checkbox');
      if (field.error) {
        container.classList.add(field.type==='checkbox' ? 'checkbox--error' : 'field--error');
        if (field.type !== 'checkbox') {
          let errEl = container.querySelector('.field__error') || document.createElement('span');
          errEl.className = 'field__error';
          errEl.textContent = field.error;
          container.appendChild(errEl);
        }
      } else {
        container.classList.remove('field--error','checkbox--error');
        const errEl = container.querySelector('.field__error');
        if (errEl) errEl.remove();
      }
    });

    if (response.message && response.status === 'error') {
      const div = document.createElement('div');
      div.className = 'form__error';
      div.textContent = response.message;
      this.form.prepend(div);
    }
  }

  // Clear fields and show success message
  _onSuccess(response) {
    const existing = this.form.querySelector('.form__success');
    if (existing) existing.remove();

    Object.values(response.fields).forEach(field => {
      if (!['hidden','honeypot'].includes(field.type)) {
        const input = this.form.querySelector(`[name="${field.name}"]`);
        if (input) { input.value = ''; input.checked = false; }
      }
    });

    if (response.message && response.status === 'success') {
      const div = document.createElement('div');
      div.className = 'form__success';
      div.textContent = response.message;
      this.form.prepend(div);
    }
  }
}

// Instantiate once DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new FormHandler('.form--builder');
});